import * as React from 'react'
import { clamp, ownerDocument, useEventCallback } from '@applift/factor'
import { GridEventListener } from '../../../models'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { useGridNativeEventListener } from '../../utils/useGridNativeEventListener'
import { CursorCoordinates } from '../../../models'
import {
  findGridCellElementsFromCol,
  findGroupHeaderElementsFromField,
  findParentElementFromClassName,
  getFieldFromHeaderElem,
} from '../../../utils/domUtils'
import { GridColumnHeaderSeparatorSides } from '../../../components/columnHeaders/GridColumnHeaderSeparator'
import style from '../../../style'
import {
  GridColumnHeaderParams,
  GridColumnResizeParams,
} from '../../../models/params'
import { defaultColumnSizing } from '../../core/useReactTable'
import { ColumnSizingState } from '../../../models/gridReactTable'

type ResizeDirection = keyof typeof GridColumnHeaderSeparatorSides
export type Direction = 'ltr' | 'rtl'

// TODO: remove support for Safari < 13.
// https://caniuse.com/#search=touch-action
//
// Safari, on iOS, supports touch action since v13.
// Over 80% of the iOS phones are compatible
// in August 2020.
// Utilizing the CSS.supports method to check if touch-action is supported.
// Since CSS.supports is supported on all but Edge@12 and IE and touch-action
// is supported on both Edge@12 and IE if CSS.supports is not available that means that
// touch-action will be supported
let cachedSupportsTouchActionNone = false
function doesSupportTouchActionNone(): boolean {
  if (cachedSupportsTouchActionNone === undefined) {
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      cachedSupportsTouchActionNone = CSS.supports('touch-action', 'none')
    } else {
      cachedSupportsTouchActionNone = true
    }
  }
  return cachedSupportsTouchActionNone
}

function trackFinger(
  event: any,
  currentTouchId: number | undefined
): CursorCoordinates | boolean {
  if (currentTouchId !== undefined && event.changedTouches) {
    for (let i = 0; i < event.changedTouches.length; i += 1) {
      const touch = event.changedTouches[i]
      if (touch.identifier === currentTouchId) {
        return {
          x: touch.clientX,
          y: touch.clientY,
        }
      }
    }

    return false
  }

  return {
    x: event.clientX,
    y: event.clientY,
  }
}

function computeNewWidth(
  initialOffsetToSeparator: number,
  clickX: number,
  columnBounds: DOMRect,
  resizeDirection: ResizeDirection
) {
  let newWidth = initialOffsetToSeparator
  if (resizeDirection === 'Right') {
    newWidth += clickX - columnBounds.left
  } else {
    newWidth += columnBounds.right - clickX
  }
  return newWidth
}

function computeOffsetToSeparator(
  clickX: number,
  columnBounds: DOMRect,
  resizeDirection: ResizeDirection
) {
  if (resizeDirection === 'Left') {
    return clickX - columnBounds.left
  }
  return columnBounds.right - clickX
}

function flipResizeDirection(side: ResizeDirection) {
  if (side === 'Right') {
    return 'Left'
  }
  return 'Right'
}

function getResizeDirection(element: HTMLElement, direction: Direction) {
  const side = element.classList.contains(style['columnSeparator--sideRight'])
    ? 'Right'
    : 'Left'
  if (direction === 'rtl') {
    // Resizing logic should be mirrored in the RTL case
    return flipResizeDirection(side)
  }
  return side
}

export const useGridColumnResize = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>
) => {
  const colDefRef = React.useRef<GridColumnHeaderParams>()
  const colElementRef = React.useRef<HTMLDivElement>()
  const colGroupingElementRef = React.useRef<Element[]>()
  const colCellElementsRef = React.useRef<Element[]>()

  // To improve accessibility, the separator has padding on both sides.
  // Clicking inside the padding area should be treated as a click in the separator.
  // This ref stores the offset between the click and the separator.
  const initialOffsetToSeparator = React.useRef<number>()
  const resizeDirection = React.useRef<ResizeDirection>()

  const stopResizeEventTimeout = React.useRef<any>()
  const touchId = React.useRef<number>()

  const updateWidth = (newWidth: number) => {
    colDefRef.current!.computedWidth = newWidth

    if (colCellElementsRef.current) {
      colElementRef.current!.style.width = `${newWidth}px`
      colElementRef.current!.style.minWidth = `${newWidth}px`
      colElementRef.current!.style.maxWidth = `${newWidth}px`
    }

    ;[
      ...colCellElementsRef.current!,
      ...colGroupingElementRef.current!,
    ].forEach(element => {
      const div = element as HTMLDivElement
      const finalWidth: `${number}px` = `${newWidth}px`

      div.style.width = finalWidth
      div.style.minWidth = finalWidth
      div.style.maxWidth = finalWidth
    })
  }

  const handleResizeMouseUp = useEventCallback((nativeEvent: MouseEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    stopListening()

    if (colDefRef.current?.field) {
      const newColumnSizing: ColumnSizingState = {}
      newColumnSizing[colDefRef.current.field] =
        colDefRef.current?.computedWidth
      apiRef.current.tableInstance.setColumnSizing(old => ({
        ...old,
        ...newColumnSizing,
      }))
    }

    clearTimeout(stopResizeEventTimeout.current)
    stopResizeEventTimeout.current = setTimeout(() => {
      apiRef.current.publishEvent('columnResizeStop', null, nativeEvent)
      if (colDefRef.current) {
        apiRef.current.publishEvent(
          'columnWidthChange',
          {
            element: colElementRef.current,
            colDef: colDefRef.current,
            width: colDefRef.current.computedWidth,
          },
          nativeEvent
        )
      }
    })
  })

  const handleResizeMouseMove = useEventCallback((nativeEvent: MouseEvent) => {
    if (nativeEvent.buttons === 0) {
      handleResizeMouseUp(nativeEvent)
      return
    }

    let newWidth = computeNewWidth(
      initialOffsetToSeparator.current!,
      nativeEvent.clientX,
      colElementRef.current!.getBoundingClientRect(),
      resizeDirection.current!
    )

    newWidth = clamp(
      newWidth,
      colDefRef.current?.minWidth ?? defaultColumnSizing.minSize,
      colDefRef.current?.maxWidth ?? defaultColumnSizing.maxSize
    )
    updateWidth(newWidth)

    const params: GridColumnResizeParams = {
      element: colElementRef.current,
      colDef: colDefRef.current!,
      width: newWidth,
    }
    apiRef.current.publishEvent('columnResize', params, nativeEvent)
  })

  const handleColumnResizeMouseDown: GridEventListener<'columnSeparatorMouseDown'> =
    useEventCallback((colDef, event) => {
      // Only handle left clicks
      if (event.button !== 0) {
        return
      }
      if (!colDef.field) {
        return
      }
      if (!colDef.canResize) {
        return
      }
      event.preventDefault()

      apiRef.current.publishEvent(
        'columnResizeStart',
        { field: colDef.field },
        event
      )
      colDefRef.current = colDef
      /* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
      colElementRef.current =
        apiRef.current.columnHeadersContainerElementRef?.current!.querySelector<HTMLDivElement>(
          `[data-field="${colDef.field}"]`
        )!

      colGroupingElementRef.current = findGroupHeaderElementsFromField(
        apiRef.current.columnHeadersContainerElementRef?.current!,
        colDef.field
      )

      colCellElementsRef.current = findGridCellElementsFromCol(
        colElementRef.current
      )
      resizeDirection.current = getResizeDirection(event.currentTarget, 'ltr')

      initialOffsetToSeparator.current = computeOffsetToSeparator(
        event.clientX,
        colElementRef.current!.getBoundingClientRect(),
        resizeDirection.current
      )
      const doc = ownerDocument(apiRef.current.rootElementRef!.current)
      doc.body.style.cursor = 'col-resize'
      doc.addEventListener('mousemove', handleResizeMouseMove)
      doc.addEventListener('mouseup', handleResizeMouseUp)
    })

  const handleTouchEnd = useEventCallback((nativeEvent: TouchEvent) => {
    const finger = trackFinger(nativeEvent, touchId.current)
    if (!finger) {
      return
    }
    stopListening()

    if (colDefRef.current?.field) {
      const newColumnSizing: ColumnSizingState = {}
      newColumnSizing[colDefRef.current.field] =
        colDefRef.current?.computedWidth
      apiRef.current.tableInstance.setColumnSizing(old => ({
        ...old,
        ...newColumnSizing,
      }))
    }

    clearTimeout(stopResizeEventTimeout.current)
    stopResizeEventTimeout.current = setTimeout(() => {
      apiRef.current.publishEvent('columnResizeStop', null, nativeEvent as any)
      if (colDefRef.current) {
        apiRef.current.publishEvent(
          'columnWidthChange',
          {
            element: colElementRef.current,
            colDef: colDefRef.current,
            width: colDefRef.current.computedWidth,
          },
          nativeEvent
        )
      }
    })
  })

  const handleTouchMove = useEventCallback((nativeEvent: TouchEvent) => {
    const finger = trackFinger(nativeEvent, touchId.current)
    if (!finger) {
      return
    }
    // @ts-ignore
    if (nativeEvent.type === 'mousemove' && nativeEvent.buttons === 0) {
      handleTouchEnd(nativeEvent)
      return
    }

    let newWidth = computeNewWidth(
      initialOffsetToSeparator.current!,
      (finger as CursorCoordinates).x,
      colElementRef.current!.getBoundingClientRect(),
      resizeDirection.current!
    )

    newWidth = clamp(
      newWidth,
      colDefRef.current?.minWidth ?? defaultColumnSizing.minSize,
      colDefRef.current?.maxWidth ?? defaultColumnSizing.maxSize
    )
    updateWidth(newWidth)

    const params: GridColumnResizeParams = {
      element: colElementRef.current,
      colDef: colDefRef.current!,
      width: newWidth,
    }
    apiRef.current.publishEvent('columnResize', params, nativeEvent as any)
  })

  const handleTouchStart = useEventCallback((event: TouchEvent) => {
    colElementRef.current = findParentElementFromClassName(
      event.target as any,
      style.columnHeader
    ) as HTMLDivElement
    const field = getFieldFromHeaderElem(colElementRef.current!)
    if (!field) {
      return
    }
    let colDef
    try {
      colDef = apiRef.current.tableInstance.getColumn(field)
      if (!colDef) {
        return
      }
    } catch (e) {
      return
    }
    if (!colDef.getCanResize()) {
      return
    }
    if (!doesSupportTouchActionNone()) {
      event.preventDefault()
    }

    apiRef.current.publishEvent(
      'columnResizeStart',
      { field: field },
      event as any
    )

    const touch = event.changedTouches[0]
    if (touch !== null) {
      // A number that uniquely identifies the current finger in the touch session.
      touchId.current = touch?.identifier
    }

    colDefRef.current = {
      canResize: colDef.getCanResize(),
      field: field,
      width: colDef.getSize(),
      maxWidth: colDef.columnDef.maxSize,
      minWidth: colDef.columnDef.minSize,
      computedWidth: colDef.getSize(),
    }

    colElementRef.current =
      apiRef.current.columnHeadersContainerElementRef?.current!.querySelector<HTMLDivElement>(
        `[data-field="${field}"]`
      )!
    colGroupingElementRef.current = findGroupHeaderElementsFromField(
      apiRef.current.columnHeadersContainerElementRef?.current!,
      field
    )
    colCellElementsRef.current = findGridCellElementsFromCol(
      colElementRef.current
    )
    resizeDirection.current = getResizeDirection(event.target as any, 'ltr')

    initialOffsetToSeparator.current = computeOffsetToSeparator(
      touch!.clientX,
      colElementRef.current!.getBoundingClientRect(),
      resizeDirection.current
    )
    const doc = ownerDocument(apiRef.current.rootElementRef!.current)
    doc.addEventListener('touchmove', handleTouchMove)
    doc.addEventListener('touchend', handleTouchEnd)
  })

  const stopListening = React.useCallback(() => {
    const doc = ownerDocument(apiRef.current.rootElementRef!.current)
    doc.body.style.removeProperty('cursor')
    doc.removeEventListener('mousemove', handleResizeMouseMove)
    doc.removeEventListener('mouseup', handleResizeMouseUp)
    doc.removeEventListener('touchmove', handleTouchMove)
    doc.removeEventListener('touchend', handleTouchEnd)
  }, [
    apiRef,
    handleResizeMouseMove,
    handleResizeMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ])

  React.useEffect(() => {
    return () => {
      clearTimeout(stopResizeEventTimeout.current)
      stopListening()
    }
  }, [apiRef, handleTouchStart, stopListening])

  useGridNativeEventListener(
    apiRef,
    () => apiRef.current.columnHeadersContainerElementRef?.current,
    'touchstart',
    handleTouchStart,
    { passive: doesSupportTouchActionNone() }
  )
  useGridApiEventHandler(
    apiRef,
    'columnSeparatorMouseDown',
    handleColumnResizeMouseDown
  )
}
